import '@angular/localize';

export type StartOptionId = 'templates' | 'import' | 'ai' | 'apps';

export type StartOption = {
  id: StartOptionId;
  title: string;
  description: string;
  bullets: string[];
  ctaLabel: string;
  recommended?: boolean;
  iconClass: string;
};

export const ONBOARDING_CONFIG = {
  texts: {
    // --- Onboarding Main Page ---
    pageTitle: $localize`:@@onboarding.pageTitle:What do you want to manage with Rapider AI?`,
    pageSubtitle: $localize`:@@onboarding.pageSubtitle:Choose how you want to start building your system and your data.`,
    footerNote: $localize`:@@onboarding.footerNote:You can always refine your models, import more data, or switch approaches later.`,
    recommendedBadge: $localize`:@@onboarding.recommendedBadge:Recommended`,

    // --- Templates Page ---
    templatesTitle: $localize`:@@templates.title:Explore Data Model Templates`,
    templatesSubtitle: $localize`:@@templates.subtitle:Start your project instantly with predefined data structures for common use cases.`,
    searchPlaceholder: $localize`:@@templates.searchPlaceholder:Search templates by name, key, or tag...`,
    emptyState: $localize`:@@templates.emptyState:No templates found matching your criteria.`,
    loadMore: $localize`:@@templates.loadMore:Load More Templates`,
    previewBtn: $localize`:@@templates.previewBtn:Preview`,

    // --- Template Preview Page ---
    previewHeaderTitle: $localize`:@@preview.headerTitle:Template Details`,
    previewCoverPageTitle: $localize`:@@preview.coverPageTitle:Template Documentation`,
    previewModelsSummaryTitle: $localize`:@@preview.modelsSummaryTitle:Data Model Summary`,
    previewModelsTitle: $localize`:@@preview.modelsTitle:Data Models`,
    previewLocalDiagramTitle: $localize`:@@preview.localDiagramTitle:Model Relations Diagram`,
    previewGlobalDiagramTitle: $localize`:@@preview.globalDiagramTitle:Overall Template Diagram`,
    previewGlobalDiagramSubtitle: $localize`:@@preview.globalDiagramSubtitle:Visual representation of all models and their relationships within this template.`,
    previewTitleTemplate: $localize`:@@preview.titleTemplate:Display Title Template`,
    previewFieldsTitle: $localize`:@@preview.fieldsTitle:Fields`,
    previewRelationsTitle: $localize`:@@preview.relationsTitle:Relations`,
    previewTableFieldName: $localize`:@@preview.tableFieldName:Name`,
    previewTableFieldType: $localize`:@@preview.tableFieldType:Type`,
    previewTableFieldDesc: $localize`:@@preview.tableFieldDesc:Description`,
    previewTableFieldFlags: $localize`:@@preview.tableFieldFlags:Flags`,
    previewTableRelName: $localize`:@@preview.tableRelName:Name`,
    previewTableRelType: $localize`:@@preview.tableRelType:Type`,
    previewTableRelTarget: $localize`:@@preview.tableRelTarget:Target Model`,
    previewTableRelDesc: $localize`:@@preview.tableRelDesc:Description`,

    // --- AI Agent Config Texts ---
    previewAiConfigTitle: $localize`:@@preview.aiConfigTitle:AI Agent Configuration`,
    previewAiVectorSearch: $localize`:@@preview.aiVectorSearch:Vector Search Fields`,
    previewAiPlanning: $localize`:@@preview.aiPlanning:Planning Operations`,
    previewAiCreate: $localize`:@@preview.aiCreate:Entity Item Create`,
    previewAiUpdate: $localize`:@@preview.aiUpdate:Entity Item Update`,
    previewAiRead: $localize`:@@preview.aiRead:Entity Read`,
    previewAiDelete: $localize`:@@preview.aiDelete:Entity Item Delete`,
    previewAiRequiredFields: $localize`:@@preview.aiRequiredFields:Required Fields`,
    previewAiImmutableFields: $localize`:@@preview.aiImmutableFields:Immutable Fields`,
    previewAiRequiredRelations: $localize`:@@preview.aiRequiredRelations:Required Relations`,
    previewAiAutoGenerate: $localize`:@@preview.aiAutoGenerate:Auto Generate Fields`,
    previewAiRequireUserConf: $localize`:@@preview.aiRequireUserConf:Require User Confirmation`,
    previewAiCustomValidation: $localize`:@@preview.aiCustomValidation:Custom Validation Prompt`,
    previewAiRestrictedStates: $localize`:@@preview.aiRestrictedStates:Restricted States`,
    previewAiPrompt: $localize`:@@preview.aiPrompt:Additional Prompt`,
    previewAiContextLimit: $localize`:@@preview.aiContextLimit:Default Context Limit`,
    previewAiPageSize: $localize`:@@preview.aiPageSize:Default Page Size`,
    previewAiHiddenFields: $localize`:@@preview.aiHiddenFields:Hidden Fields`,
    previewAiMaskedFields: $localize`:@@preview.aiMaskedFields:Masked Fields`,
    previewAiCaptchaConf: $localize`:@@preview.aiCaptchaConf:Require Captcha Confirmation`,
    previewAiCascading: $localize`:@@preview.aiCascading:Cascading Relations`,

    // --- Add to Workspace Action ---
    addTemplateBtn: $localize`:@@preview.addTemplateBtn:Integrate Template into My Workspace`,
    confirmModalTitle: $localize`:@@preview.confirmModalTitle:Integrate Template`,
    confirmModalDesc: $localize`:@@preview.confirmModalDesc:The listed data models will be integrated into your workspace. You will be able to manage the data of these models with AI.`,
    confirmModalCancel: $localize`:@@preview.confirmModalCancel:Cancel`,
    confirmModalConfirm: $localize`:@@preview.confirmModalConfirm:Yes, Integrate Template`,
    processingTitle: $localize`:@@preview.processingTitle:Integrating Template...`,
    processingDesc: $localize`:@@preview.processingDesc:Please wait while we set up the data models in your workspace. This might take a moment.`,

    // --- AI Integration Timeline Texts ---
    integrationPageTitle: $localize`:@@integration.pageTitle:Integrate Template`,
    integrationPageSubtitle: $localize`:@@integration.pageSubtitle:Review the template details below before confirming the integration into your active workspace.`,
    integrationStep1Title: $localize`:@@integration.step1Title:Confirm Template`,
    integrationStep1Desc: $localize`:@@integration.step1Desc:Please confirm that you want to integrate this template library with your project.`,
    integrationBtnYes: $localize`:@@integration.btnYes:Yes, Integrate Template`,
    integrationBtnNo: $localize`:@@integration.btnNo:No, Cancel`,
    integrationSuccessTitle: $localize`:@@integration.successTitle:Integration Successful`,
    integrationSuccessDesc: $localize`:@@integration.successDesc:The template has been successfully integrated into your database.`,
    integrationErrorTitle: $localize`:@@integration.errorTitle:Integration Failed`,
    integrationErrorDesc: $localize`:@@integration.errorDesc:There was an error integrating the template. Please try again.`,
    integrationNavigateDbBtn: $localize`:@@integration.navigateDbBtn:Navigate to Database`,

    // Relation Cards Texts
    integrationSuggestedRelationsTitle: $localize`:@@integration.suggestedRelationsTitle:Suggested Relationships to Add`,
  },
  notifications: {
    fetchFailed: $localize`:@@templates.fetchFailed:Failed to load templates. Please try again.`,
    fetchPreviewFailed: $localize`:@@preview.fetchFailed:Failed to load template details.`,
    addSuccess: $localize`:@@preview.addSuccess:Template successfully added to your workspace.`,
    addFailed: $localize`:@@preview.addFailed:Failed to add template to workspace.`,
    integrationAnalysisFailed: $localize`:@@integration.analysisFailed:Failed to analyze integration with AI. Please try again.`,
  },
};

/**
 * Pure function to build the payload for processing a data model template.
 * Constructs the object matching the SDK requirements to process a definition.
 * @param {string} definitionId - The ID of the template definition to process.
 * @returns {any} The formatted payload object for the SDK.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildProcessTemplatePayload(definitionId: string): any {
  return {
    body: {
      definitionId: definitionId,
    },
  };
}

/**
 * PURE FUNCTION: Builds a compact string representation of the current project models for AI context.
 * Extracts necessary fields and relations to conserve token limits while providing structural awareness.
 * @param {any[]} models - The array of current project models.
 * @param {boolean} excludeAuditFields - If true, filters out fields with isMixinProperty: true. Defaults to false.
 * @returns {string} Compact context string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildCompactProjectModelsContext(
  models: any[],
  excludeAuditFields: boolean = false,
): string {
  if (!models || models.length === 0) return 'No existing models in project.';

  return models
    .map((m) => {
      // Process Fields
      let fieldsArr = m.fields || [];
      if (excludeAuditFields) {
        fieldsArr = fieldsArr.filter((f: any) => !f.isMixinProperty);
      }
      const fields =
        'id (uuid, string), ' +
        fieldsArr
          .map((f: any) => `${f.name} (${f.type}${f.isArray ? '[]' : ''})`)
          .join(', ');

      // Process Relations
      const relationsArr = m.relations || [];
      const relations = relationsArr
        .map((r: any) => {
          const targetName =
            r.targetModel?.name ||
            r.targetModel?.title ||
            r.targetModel ||
            'Unknown';
          return `${r.name} (${r.type} -> ${targetName})`;
        })
        .join(', ');

      return `Model: ${m.name}\nDescription: ${m.description || 'N/A'}\nFields: ${fields || 'None'}\nRelations: ${relations || 'None'}`;
    })
    .join('\n\n');
}

/**
 * PURE FUNCTION: Builds a highly structured, token-efficient string representation of the Template Library.
 * Maps out general template information, followed by clearly demarcated models, fields, and relations.
 * @param {any} templateLibrary - The raw template library object.
 * @returns {string} The formatted and highly readable template context string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildCompactTemplateLibraryContext(
  templateLibrary: any,
): string {
  if (!templateLibrary) return 'No template library provided.';

  // Resolve the actual models payload
  const libraryPayload = templateLibrary.value || templateLibrary;
  const models = libraryPayload.dataModels || libraryPayload.models || [];

  let context = `Template Name: ${libraryPayload.name || templateLibrary.name || 'Unknown'}\n`;
  context += `Description: ${libraryPayload.description || templateLibrary.description || 'N/A'}\n\n`;

  models.forEach((m: any) => {
    context += `### Model: ${m.name}\n`;
    context += `Title: ${m.title || 'N/A'}\n`;
    context += `Description: ${m.description || 'N/A'}\n`;
    context += `Domain Name: ${m.domainName || 'N/A'}\n`;
    context += `Domain Key: ${m.domainKey || 'N/A'}\n\n`;

    if (m.fields && m.fields.length > 0) {
      context += `#### Fields:\n`;
      m.fields.forEach((f: any) => {
        context += `${f.name}: ${f.description || 'No description provided.'} (required: ${!!f.isRequired})\n`;
      });
      context += `\n`;
    }

    if (m.relations && m.relations.length > 0) {
      context += `#### Relations:\n`;
      m.relations.forEach((r: any) => {
        context += `${r.name}: \`${r.sourceModel} ${r.type} ${r.targetModel}\`\n`;
        context += `${r.description || 'No description provided.'}\n`;
      });
      context += `\n`;
    }

    context += `-----\n\n`;
  });

  return context.trim();
}

/**
 * PURE FUNCTION: Builds a structured prompt for the AI agent to analyze and integrate a template library into a project.
 * Provides the current project context and the target template library in a token-optimized format.
 * @param {any[]} currentModels - The current project models.
 * @param {any} templateLibrary - The template library data object.
 * @param {string} [userFeedback] - Optional user feedback for subsequent refinements.
 * @param {boolean} [excludeAuditFields=false] - Whether to exclude audit fields from the prompt context.
 * @returns {string} The formatted prompt string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildIntegrationPrompt(
  currentModels: any[],
  templateLibrary: any,
  userFeedback?: string,
  excludeAuditFields: boolean = false,
): string {
  const projectContext = buildCompactProjectModelsContext(
    currentModels,
    excludeAuditFields,
  );
  const libraryContext = buildCompactTemplateLibraryContext(templateLibrary);

  let prompt = `
    <Existing Models>

    ${projectContext}

    </Existing Models>

    -----

    <New Models>:
    ${libraryContext}
    </New Models>

    -----
    `;

  if (userFeedback) {
    prompt += `\nUser Feedback for Adjustments:\n"${userFeedback}"\nApply this feedback to the generated relations.\n`;
  }

  return prompt;
}

/**
 * Pure function to safely check if an AI Agent Config object is present and contains actual configuration keys.
 * @param {any} config - The AI Agent Configuration object.
 * @returns {boolean} True if the configuration exists and is not empty.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasDetailedAiConfig(config: any): boolean {
  return config && typeof config === 'object' && Object.keys(config).length > 0;
}

/**
 * Pure function to map a local graph data structure for a specific central model.
 * It isolates the main model and places related models around it to prevent line chaos.
 * @param {string} mainModelName - The target model to center the graph around.
 * @param {any[]} allModels - The full list of models to search for relations.
 * @returns {{nodes: any[], edges: any[]}} An object containing the configured X6 nodes and edges.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildLocalGraphData(
  mainModelName: string,
  allModels: any[],
): { nodes: any[]; edges: any[] } {
  const mainModel = allModels.find((m) => m.name === mainModelName);
  if (!mainModel) return { nodes: [], edges: [] };

  const nodes: any[] = [];
  const edges: any[] = [];

  nodes.push({
    id: mainModel.name,
    shape: 'rect',
    x: 0,
    y: 0,
    width: 220,
    height: 54,
    label: mainModel.title || mainModel.name,
    attrs: {
      body: {
        fill: 'var(--color-primary)',
        stroke: 'var(--color-primary)',
        strokeWidth: 2,
        rx: 8,
        ry: 8,
      },
      label: {
        fill: 'var(--color-primary-button-text)',
        fontSize: 15,
        fontWeight: 'bold',
      },
    },
  });

  const relatedModelNames = new Set<string>();

  if (mainModel.relations) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mainModel.relations.forEach((rel: any) => {
      if (rel.targetModel) relatedModelNames.add(rel.targetModel);
      if (rel.throughModel) relatedModelNames.add(rel.throughModel);
    });
  }

  allModels.forEach((m) => {
    if (m.name !== mainModelName && m.relations) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      m.relations.forEach((rel: any) => {
        if (
          rel.targetModel === mainModelName ||
          rel.throughModel === mainModelName
        ) {
          relatedModelNames.add(m.name);
        }
      });
    }
  });

  relatedModelNames.delete(mainModelName);
  const relatedArray = Array.from(relatedModelNames);

  const spacingX = 260;
  const startX = -((relatedArray.length - 1) * spacingX) / 2;

  relatedArray.forEach((relName, index) => {
    const relModel = allModels.find((m) => m.name === relName);
    nodes.push({
      id: relName,
      shape: 'rect',
      x: startX + index * spacingX,
      y: 160,
      width: 180,
      height: 48,
      label: relModel?.title || relName,
      attrs: {
        body: {
          fill: 'var(--color-component-inner)',
          stroke: 'var(--color-secondary-border)',
          strokeWidth: 2,
          rx: 8,
          ry: 8,
        },
        label: {
          fill: 'var(--color-primary-text)',
          fontSize: 13,
          fontWeight: '600',
        },
      },
    });
  });

  const outwardTargets = new Set<string>();

  if (mainModel.relations) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mainModel.relations.forEach((rel: any) => {
      edges.push(
        createGraphEdge(
          rel.sourceModel,
          rel.targetModel,
          rel.name,
          rel.type,
          rel.throughModel,
        ),
      );
      outwardTargets.add(rel.targetModel);
    });
  }

  allModels.forEach((m) => {
    if (m.name !== mainModelName && m.relations) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      m.relations.forEach((rel: any) => {
        if (rel.targetModel === mainModelName) {
          if (!outwardTargets.has(m.name)) {
            edges.push(
              createGraphEdge(
                rel.sourceModel,
                rel.targetModel,
                rel.name,
                rel.type,
                rel.throughModel,
              ),
            );
          }
        }
      });
    }
  });

  return { nodes, edges };
}

/**
 * Pure function to filter relationships for the global diagram.
 * Ensures only the highest priority relationship is drawn between any two models to prevent overlapping lines.
 * Priority: hasManyThrough > hasMany > hasOne > referencesMany > belongsTo.
 * @param {any[]} relations - The unfiltered array of relations.
 * @returns {any[]} The filtered array of relations.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function filterGlobalEdges(relations: any[]): any[] {
  const pairMap = new Map<string, any[]>();

  relations.forEach((rel) => {
    if (!rel.sourceModel || !rel.targetModel) return;
    // Create a direction-agnostic key for the model pair
    const pairKey = [rel.sourceModel, rel.targetModel].sort().join('-');
    const existing = pairMap.get(pairKey) || [];
    existing.push(rel);
    pairMap.set(pairKey, existing);
  });

  const priority: Record<string, number> = {
    hasManyThrough: 1,
    hasMany: 2,
    hasOne: 3,
    referencesMany: 4,
    belongsTo: 5,
  };

  const filtered: any[] = [];

  pairMap.forEach((rels) => {
    // Sort relationships by defined priority
    rels.sort((a, b) => (priority[a.type] || 99) - (priority[b.type] || 99));
    // Pick only the top priority relationship to draw
    filtered.push(rels[0]);
  });

  return filtered;
}

/**
 * Pure function to map a global graph data structure for all models in the template.
 * @param {any[]} allModels - The full list of models to visualize.
 * @returns {{nodes: any[], edges: any[]}} An object containing the configured X6 nodes and edges.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildGlobalGraphData(allModels: any[]): {
  nodes: any[];
  edges: any[];
} {
  const nodes: any[] = [];
  const edges: any[] = [];

  if (!allModels || !Array.isArray(allModels)) return { nodes, edges };

  const cols = 3;
  const spacingX = 350;
  const spacingY = 250;

  const allRelations: any[] = [];

  allModels.forEach((model, index) => {
    nodes.push({
      id: model.name,
      shape: 'rect',
      x: (index % cols) * spacingX + 50,
      y: Math.floor(index / cols) * spacingY + 50,
      width: 200,
      height: 50,
      label: model.title || model.name,
      attrs: {
        body: {
          fill: 'var(--color-component-inner)',
          stroke: 'var(--color-primary)',
          strokeWidth: 2,
          rx: 8,
          ry: 8,
        },
        label: {
          fill: 'var(--color-primary-text)',
          fontSize: 14,
          fontWeight: '600',
        },
      },
    });

    if (model.relations && Array.isArray(model.relations)) {
      allRelations.push(...model.relations);
    }
  });

  // Filter the relations to only show the most important one between any two models
  const filteredRelations = filterGlobalEdges(allRelations);

  filteredRelations.forEach((rel) => {
    edges.push(
      createGraphEdge(
        rel.sourceModel,
        rel.targetModel,
        rel.name,
        rel.type,
        rel.throughModel,
      ),
    );
  });

  return { nodes, edges };
}

/**
 * Pure function to format a standard edge definition.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createGraphEdge(
  source: string,
  target: string,
  labelName: string,
  labelType: string,
  throughModel?: string,
): any {
  const labelText =
    labelType === 'hasManyThrough'
      ? `${source} has many ${target}\nthrough ${throughModel}`
      : `${source} ${labelType} ${target}`;
  return {
    source,
    target,
    router: { name: 'manhattan', args: { padding: 30 } },
    connector: { name: 'rounded', args: { radius: 8 } },
    attrs: {
      line: {
        stroke: 'var(--color-secondary-text)',
        strokeWidth: 2,
        targetMarker: {
          name: 'classic',
          size: 8,
          fill: 'var(--color-secondary-text)',
        },
      },
    },
    labels: [
      {
        attrs: {
          text: {
            text: labelText,
            fill: 'var(--color-secondary-text)',
            fontSize: 11,
            textAnchor: 'middle',
            textVerticalAnchor: 'middle',
          },
          rect: {
            fill: 'var(--color-body-background)',
            rx: 8,
            ry: 8,
            stroke: 'var(--color-secondary-border)',
            strokeWidth: 1,
          },
        },
      },
    ],
  };
}

/**
 * Pure function to safely check if the typed key matches the template's actual key.
 * @param {string} actualKey - The actual key of the template.
 * @param {string} inputKey - The text inputted by the user.
 * @returns {boolean} True if they match perfectly.
 */
export function isDeleteKeyMatch(actualKey: string, inputKey: string): boolean {
  if (!actualKey || !inputKey) return false;
  return actualKey === inputKey.trim();
}

/**
 * Pure function to initialize the expanded state of model cards so they are open by default.
 * @param {any[]} models - The array of incoming models.
 * @returns {Record<string, boolean>} A map of model names set to true.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initializeExpandedState(
  models: any[],
): Record<string, boolean> {
  return models.reduce((acc, curr) => ({ ...acc, [curr.name]: true }), {});
}

/**
 * Pure function to toggle the boolean value of a specific key within a state record.
 * @param {Record<string, boolean>} currentState - The current dictionary state.
 * @param {string} key - The key to toggle.
 * @returns {Record<string, boolean>} A newly constructed record with the toggled value.
 */
export function toggleRecordState(
  currentState: Record<string, boolean>,
  key: string,
): Record<string, boolean> {
  return { ...currentState, [key]: !currentState[key] };
}

/**
 * Pure function to generate the list of onboarding start options.
 * This guarantees a fresh, localized array without depending on global state.
 * @returns {StartOption[]} An array of structured start options.
 */
export function getStartOptions(): StartOption[] {
  return [
    {
      id: 'apps',
      title: $localize`:@@onboarding.opt.apps.title:App Store`,
      description: $localize`:@@onboarding.opt.apps.desc:Browse our App Store to find ready-to-use, complete applications tailored for various industries and use cases.`,
      bullets: [
        $localize`:@@onboarding.opt.apps.b1:Fully functional applications`,
        $localize`:@@onboarding.opt.apps.b2:Pre-configured UI and data models`,
        $localize`:@@onboarding.opt.apps.b3:Install with one click`,
      ],
      ctaLabel: $localize`:@@onboarding.opt.apps.cta:Browse App Store`,
      iconClass: 'fad fa-layer-group',
      recommended: true,
    },
    {
      id: 'import',
      title: $localize`:@@onboarding.opt.import.title:Import Existing Data`,
      description: $localize`:@@onboarding.opt.import.desc:Bring in spreadsheets, files, or databases and turn them into structured data models you can manage with AI.`,
      bullets: [
        $localize`:@@onboarding.opt.import.b1:CSV, Excel, JSON, MongoDB, SQL`,
        $localize`:@@onboarding.opt.import.b2:AI-assisted field mapping`,
        $localize`:@@onboarding.opt.import.b3:Great for migrations`,
      ],
      ctaLabel: $localize`:@@onboarding.opt.import.cta:Import Data`,
      iconClass: 'fad fa-file-import',
    },
    {
      id: 'ai',
      title: $localize`:@@onboarding.opt.ai.title:Start from Scratch with AI`,
      description: $localize`:@@onboarding.opt.ai.desc:Describe what you want to manage, and AI will help define your models, fields, and relationships.`,
      bullets: [
        $localize`:@@onboarding.opt.ai.b1:Chat-based setup`,
        $localize`:@@onboarding.opt.ai.b2:AI-generated model suggestions`,
        $localize`:@@onboarding.opt.ai.b3:Best for custom workflows`,
      ],
      ctaLabel: $localize`:@@onboarding.opt.ai.cta:Start with AI`,
      iconClass: 'fad fa-sparkles',
    },
    {
      id: 'templates',
      title: $localize`:@@onboarding.opt.templates.title:Explore Templates`,
      description: $localize`:@@onboarding.opt.templates.desc:Start with ready-made data models for common use cases like task management, ecommerce, CRM, and invoicing.`,
      bullets: [
        $localize`:@@onboarding.opt.templates.b1:Ready-made models and fields`,
        $localize`:@@onboarding.opt.templates.b2:Prebuilt APIs and views`,
        $localize`:@@onboarding.opt.templates.b3:Fastest way to get started`,
      ],
      ctaLabel: $localize`:@@onboarding.opt.templates.cta:Browse Templates`,
      iconClass: 'fad fa-layer-group',
      recommended: false,
    },
  ];
}

/**
 * Pure function to map a StartOptionId to its corresponding application route.
 * @param {StartOptionId} optionId - The identifier of the chosen onboarding path.
 * @returns {string} The path to navigate the user to.
 */
export function getRouteForStartOption(optionId: StartOptionId): string {
  switch (optionId) {
    case 'apps':
      return '/app-store';
    case 'templates':
      return '/data-templates';
    case 'import':
      return '/import-data';
    case 'ai':
      return '/ai-setup';
    default:
      return '/home';
  }
}

/**
 * Pure function to build a dynamic LoopBack filter for the templates query.
 * Excludes heavy properties like `value`, `isPublic`, and `type` to optimize payload.
 * Incorporates search logic across the 'name', 'key', and 'tags' fields.
 * @param {number} pageSize - Maximum number of templates to return.
 * @param {number} skip - Number of templates to skip for pagination.
 * @param {string} searchQuery - The text to search for within template attributes.
 * @returns {string} Stringified JSON filter for the API.
 */
export function buildTemplatesFilter(
  pageSize: number,
  skip: number,
  searchQuery: string,
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filter: any = {
    where: { type: 'dataModels' },
    order: ['priorityIndex ASC'],
    limit: pageSize,
    skip: skip,
    fields: {
      id: true,
      name: true,
      key: true,
      description: true,
      tags: true,
      priorityIndex: true,
      // Explicitly excluding heavy fields to minimize payload
      value: false,
      isPublic: false,
      type: false,
    },
  };

  if (searchQuery && searchQuery.trim() !== '') {
    const query = searchQuery.trim();
    filter.where = {
      and: [
        { type: 'dataModels' },
        {
          or: [
            { name: { like: `.*${query}.*`, options: 'i' } },
            { key: { like: `.*${query}.*`, options: 'i' } },
            // Searches inside the tags array
            { tags: { like: `.*${query}.*`, options: 'i' } },
          ],
        },
      ],
    };
  }

  return JSON.stringify(filter);
}
